import { useState, ReactNode, useEffect } from "react";
import { Form, Col, Button, Row } from "react-bootstrap";
import moment from "moment-timezone";
import messages from "messages";
// constants
import { STATE_OPTIONS } from "constants/stateOptions";
// components
import CustomSelect from "components/custom-select/CustomSelect";
import FormControlWithMask from "components/utility/FormControlWithMask";
import AddExistingContact from "../add-existing-contact";
import { NATIVE_DATE_INPUT_FORMAT } from "components/patient-editor/constants/dobFormat";
import { PHONE_NUMBER_MASK } from "constants/phoneNumberMask";
// types
import type { PatientForPatientEditor } from "components/patient-editor/types";
// styles
import styles from "./PatientInfoEditor.module.scss";
// hooks
import { useUser } from "contexts/UserContext";
import { AmplitudePageName, track } from "utils/amplitude";

type Props = {
  value: Partial<PatientForPatientEditor>;
  onChange: (arg: Partial<PatientForPatientEditor>) => void;
  label?: ReactNode;
  labelClass?: string;
  className?: string;
  disableSelectFromExisting?: boolean;
  coupleOrContactUser?: PatientChartContact | null;
  type?: string;
  amplitudePageName: AmplitudePageName;
  amplitudeComponentName?: string;
};

const additionalFields = [
  "phone_number",
  "dob",
  "home_address_line1",
  "home_city",
  "home_state",
  "home_zipcode",
];

const AMPLITUDE_COMPONENT_NAME = "patient-info-editor";

/**
 * renders a stateless UI for patient info creation and edit
 */
const PatientInfoEditor = ({
  value,
  onChange,
  label = messages.patientInformation,
  labelClass,
  className,
  disableSelectFromExisting,
  coupleOrContactUser,
  type,
  amplitudePageName,
  amplitudeComponentName,
}: Props) => {
  const componentBreadCrumbs = amplitudeComponentName
    ? [amplitudeComponentName, AMPLITUDE_COMPONENT_NAME]
    : [AMPLITUDE_COMPONENT_NAME];

  const { defaultUserTimezone } = useUser();
  const [addAdditionalFields, setAddAdditionalFields] = useState(false);
  const onInputChange = (event) => {
    const val = event.currentTarget.value;
    const name = event.currentTarget.name;
    onChange({ ...value, [name]: val });
  };

  // should expand the fields automatically, if any field is filled
  useEffect(() => {
    if (
      value &&
      additionalFields.some((el) => value[el] !== undefined) &&
      !coupleOrContactUser?.email
    ) {
      setAddAdditionalFields(true);
    }
  }, [value, coupleOrContactUser?.email]);

  return (
    <div className={className}>
      <div className={labelClass}>
        {label}

        {!disableSelectFromExisting && (
          <AddExistingContact
            onApply={(e) => {
              track(
                amplitudePageName,
                [...componentBreadCrumbs, "add-existing-contact"],
                "click",
              );
              onChange(e);
            }}
          />
        )}
      </div>
      {/* <div> {messages.patientInformation + " content"}</div> */}
      <div>
        <Row>
          <Form.Group as={Col} md="6">
            <Form.Label className={styles["patient-info-editor__label"]}>
              First Name <span className="required">*</span>
            </Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Enter First Name"
              name="first_name"
              value={value?.first_name || ""}
              onChange={onInputChange}
              onFocus={() => {
                track(
                  amplitudePageName,
                  [...componentBreadCrumbs, "first-name-field"],
                  "focus",
                );
              }}
            />
          </Form.Group>
          <Form.Group as={Col} md="6">
            <Form.Label className={styles["patient-info-editor__label"]}>
              Last Name <span className="required">*</span>
            </Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Enter Last Name"
              name="last_name"
              value={value?.last_name || ""}
              onChange={onInputChange}
              onFocus={() => {
                track(
                  amplitudePageName,
                  [...componentBreadCrumbs, "last-name-field"],
                  "focus",
                );
              }}
            />
          </Form.Group>
        </Row>
        <Form.Group>
          <Form.Label className={styles["patient-info-editor__label"]}>
            Email Address
          </Form.Label>
          <Form.Control
            name="email"
            type="text"
            value={value?.email || ""}
            placeholder={"Enter Email Address"}
            onChange={onInputChange}
            disabled={!!coupleOrContactUser?.email}
            className={styles["patient-info-editor__form-control"]}
            onFocus={() => {
              track(
                amplitudePageName,
                [...componentBreadCrumbs, "email-field"],
                "focus",
              );
            }}
          />
        </Form.Group>

        {!!coupleOrContactUser?.email ? (
          <div className={"rg-12 gray01 mb-3"}>
            You cannot edit the email of the existing patient when adding to a
            Couple.
          </div>
        ) : (
          <div className={"rg-12 gray01 mb-3"}>
            To invite this patient to Orchid and your session, enter an email
            address. Your patient will receive the session invitation via email
            for confirmation.
          </div>
        )}

        {!addAdditionalFields && (
          <Button
            className={"p-0"}
            onClick={() => {
              setAddAdditionalFields(!addAdditionalFields);
              track(
                amplitudePageName,
                [...componentBreadCrumbs, "add-phone-dob-address-button"],
                "focus",
              );
            }}
            variant={"link"}
          >
            + Add Phone, DOB , Address
          </Button>
        )}

        {addAdditionalFields && (
          <div>
            <hr />
            <Row>
              <Form.Group as={Col} md="6">
                <Form.Label className={styles["patient-info-editor__label"]}>
                  Phone
                </Form.Label>
                <FormControlWithMask
                  name="phone_number"
                  mask={PHONE_NUMBER_MASK}
                  maskChar="-"
                  maskRegex="\d"
                  value={value?.phone_number || ""}
                  onChange={onInputChange}
                  onFocus={() => {
                    track(
                      amplitudePageName,
                      [...componentBreadCrumbs, "phone-field"],
                      "focus",
                    );
                  }}
                />
              </Form.Group>
              <Form.Group as={Col} md="6">
                <Form.Label className={styles["patient-info-editor__label"]}>
                  {messages.dateOfBirth}
                </Form.Label>
                <Form.Control
                  className={
                    !value?.dob ? styles["patient-info-editor__dob--empty"] : ""
                  }
                  type="date"
                  min={moment()
                    .tz(defaultUserTimezone)
                    .subtract(100, "year")
                    .format(NATIVE_DATE_INPUT_FORMAT)}
                  max={
                    type === "minor"
                      ? new Date().toISOString().split("T")[0]
                      : moment()
                          .tz(defaultUserTimezone)
                          .subtract(13, "year")
                          .format(NATIVE_DATE_INPUT_FORMAT)
                  }
                  pattern="^(0[1-9]|1[012])/(0[1-9]|[12][0-9]|3[01])/(19|20)\d\d$"
                  placeholder="mm/dd/yyyy"
                  name="dob"
                  value={
                    value?.dob
                      ? moment
                          .tz(value.dob, defaultUserTimezone)
                          .format(NATIVE_DATE_INPUT_FORMAT)
                      : ""
                  }
                  onChange={onInputChange}
                  onFocus={() => {
                    track(
                      amplitudePageName,
                      [...componentBreadCrumbs, "dob-field"],
                      "focus",
                    );
                  }}
                />
              </Form.Group>
            </Row>
            <hr />
            <Form.Group>
              <Form.Label className={styles["patient-info-editor__label"]}>
                Street Address
              </Form.Label>
              <Form.Control
                name="home_address_line1"
                type="text"
                value={value?.home_address_line1 || ""}
                placeholder={"Enter Address"}
                onChange={onInputChange}
                onFocus={() => {
                  track(
                    amplitudePageName,
                    [...componentBreadCrumbs, "street-address-field"],
                    "focus",
                  );
                }}
              />
            </Form.Group>
            <Row>
              <Form.Group md="6" as={Col}>
                <Form.Label className={styles["patient-info-editor__label"]}>
                  City
                </Form.Label>
                <Form.Control
                  name="home_city"
                  type="text"
                  placeholder={"Select City"}
                  value={value?.home_city || ""}
                  onChange={onInputChange}
                  onFocus={() => {
                    track(
                      amplitudePageName,
                      [...componentBreadCrumbs, "city-field"],
                      "focus",
                    );
                  }}
                />
              </Form.Group>
              <Form.Group md="6" as={Col}>
                <Form.Label className={styles["patient-info-editor__label"]}>
                  State
                </Form.Label>
                <CustomSelect
                  options={STATE_OPTIONS}
                  onChange={(el) =>
                    onChange({ ...value, home_state: el.value })
                  }
                  value={STATE_OPTIONS.find(
                    (option) => option.value === value?.home_state,
                  )}
                  onFocus={() => {
                    track(
                      amplitudePageName,
                      [...componentBreadCrumbs, "state-field"],
                      "focus",
                    );
                  }}
                />
              </Form.Group>
            </Row>
            <Row>
              <Form.Group md="6" as={Col}>
                <Form.Label className={styles["patient-info-editor__label"]}>
                  Zip
                </Form.Label>
                <Form.Control
                  name="home_zipcode"
                  type="text"
                  placeholder={"Enter Zipcode"}
                  value={value?.home_zipcode || ""}
                  onChange={onInputChange}
                  onFocus={() => {
                    track(
                      amplitudePageName,
                      [...componentBreadCrumbs, "zip-field"],
                      "focus",
                    );
                  }}
                />
              </Form.Group>
            </Row>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientInfoEditor;
